import { Avatar, AvatarFallback, getAvatarColor } from '@heidi/design-system/avatar';
import { Button } from '@heidi/design-system/button';
import { typographyVariant } from '@heidi/design-system/typography';
import { cn, getInitials } from '@heidi/utils';
import { UserRound } from 'lucide-react';
import { useIntl } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import type {
  ObjectWorkspaceObject,
  ObjectWorkspacePatientReference,
} from './objectWorkspace.data';

interface ObjectWorkspacePatientIdentityRowProps {
  patient?: ObjectWorkspacePatientReference;
  onObjectChange: (object: ObjectWorkspaceObject) => void;
  onAddPatient?: (anchorElement: HTMLElement) => void;
}

const NOOP = () => undefined;

// One compact, content-width pill button for both states — the hover
// background never stretches to the full row width.
const PILL_BUTTON_CLASSNAME = 'h-auto max-w-full min-w-0 gap-1.5 rounded-full px-2 py-1';

const toPatientObject = (
  patient: ObjectWorkspacePatientReference
): ObjectWorkspaceObject => ({
  id: patient.id,
  kind: 'patient',
  name: patient.name,
  ...(patient.sourceSessionId ? { sourceSessionId: patient.sourceSessionId } : {}),
  ...(patient.patientProfileId ? { patientProfileId: patient.patientProfileId } : {}),
});

export function ObjectWorkspacePatientIdentityRow({
  patient,
  onObjectChange,
  onAddPatient = NOOP,
}: ObjectWorkspacePatientIdentityRowProps): JSX.Element {
  const intl = useIntl();

  if (!patient) {
    return (
      <div className="mt-2 min-w-0" data-testid="object-workspace-patient-identity">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          flush="all"
          className={PILL_BUTTON_CLASSNAME}
          onClick={(event) => onAddPatient(event.currentTarget)}
        >
          <Avatar
            size="sm"
            color="empty"
            aria-hidden="true"
            data-testid="object-workspace-patient-avatar"
          >
            <AvatarFallback icon={<UserRound aria-hidden />} />
          </Avatar>
          <span
            className={cn(
              typographyVariant({ variant: 'p2' }),
              'min-w-0 truncate text-text-secondary'
            )}
          >
            {intl.formatMessage(messages.addPatient)}
          </span>
        </Button>
      </div>
    );
  }

  const sexInitial = patient.sex?.trim().charAt(0).toLocaleUpperCase();
  const hasAge = typeof patient.age === 'number' && Number.isFinite(patient.age);
  const displayName =
    sexInitial && hasAge
      ? intl.formatMessage(messages.patientIdentityNameSexAge, {
          name: patient.name,
          sexInitial,
          age: patient.age,
        })
      : sexInitial
        ? intl.formatMessage(messages.patientIdentityNameSex, {
            name: patient.name,
            sexInitial,
          })
        : hasAge
          ? intl.formatMessage(messages.patientIdentityNameAge, {
              name: patient.name,
              age: patient.age,
            })
          : patient.name;

  return (
    <div className="mt-2 min-w-0" data-testid="object-workspace-patient-identity">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        flush="all"
        className={PILL_BUTTON_CLASSNAME}
        aria-label={intl.formatMessage(messages.openPatientWorkspace, {
          name: patient.name,
        })}
        onClick={() => onObjectChange(toPatientObject(patient))}
      >
        <Avatar
          size="sm"
          color={getAvatarColor(patient.name)}
          aria-hidden="true"
          data-testid="object-workspace-patient-avatar"
        >
          <AvatarFallback>{getInitials(patient.name)}</AvatarFallback>
        </Avatar>
        <span
          className={cn(
            typographyVariant({ variant: 'p2' }),
            'min-w-0 truncate text-text-primary'
          )}
          title={displayName}
        >
          {displayName}
        </span>
      </Button>
    </div>
  );
}
